import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { trackGA } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, ShieldCheck, CheckCircle2, Clock,
  Zap, Sparkles, Lock, BellRing, LayoutDashboard, Globe, MapPin,
} from "lucide-react";
import { Logo } from "@/components/Logo";

// Compliance: exact SMS consent disclosure shown to screen companies on signup.
// Stored in the consent log when sms_consent === true.
const SIGNUP_SMS_CONSENT_TEXT = "By checking this box, I agree to receive transactional SMS messages from WindowScreenQuote.com about new lead alerts, account notifications, customer support, and operational updates. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for assistance. SMS consent is not shared with third parties or affiliates for marketing purposes.";

export default function Signup() {
  const [form, setForm] = useState({ business_name: "", owner_name: "", email: "", password: "", phone: "", sms_consent: false });
  const [loading, setLoading] = useState(false);
  const [invitePreview, setInvitePreview] = useState(null);
  const { signup, formatApiErrorDetail } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite_token") || "";

  useEffect(() => {
    trackGA("signup_started", { has_invite: !!inviteToken }, { once: true });
  }, [inviteToken]);

  useEffect(() => {
    if (!inviteToken) return;
    api.get(`/public/invites/${inviteToken}`)
      .then((r) => setInvitePreview(r.data))
      .catch(() => setInvitePreview({ invalid: true }));
  }, [inviteToken]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await signup({
        ...form,
        consent_text: form.sms_consent ? SIGNUP_SMS_CONSENT_TEXT : "",
        invite_token: inviteToken || undefined,
      });
      if (data?.claimed_lead_id) {
        trackGA("signup_completed", { method: "email", claimed_lead: true });
        toast.success("Account created! Your claimed lead is waiting in your dashboard.");
        nav("/dashboard");
      } else {
        trackGA("signup_completed", { method: "email", claimed_lead: false });
        toast.success("Account created! Start your $1 trial to launch.");
        nav("/billing");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-[#FBFAF6]">
      {/* Top bar */}
      <nav className="px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between border-b border-slate-200/60 bg-white/80 backdrop-blur sticky top-0 z-40">
        <Link to="/" className="flex items-center gap-2.5"><Logo size={36} withWordmark /></Link>
        <Link to="/login" className="text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="signup-signin-link">Sign in</Link>
      </nav>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10 lg:py-14 grid lg:grid-cols-12 gap-10 lg:gap-14">
        {/* LEFT — the sales pitch */}
        <section className="lg:col-span-7 order-2 lg:order-1">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-5" data-testid="signup-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>

          {!inviteToken && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400/95 text-amber-950 text-[11px] font-extrabold uppercase tracking-widest mb-5">
            <Sparkles className="w-3.5 h-3.5" /> Founder pricing — $1 for 30 days, then $97/mo locked in
          </div>
          )}
          {inviteToken && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/95 text-white text-[11px] font-extrabold uppercase tracking-widest mb-5">
            <Sparkles className="w-3.5 h-3.5" /> This lead is yours — free · no card required
          </div>
          )}

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05]" data-testid="signup-h1">
            Turn the traffic<br className="hidden sm:block" />
            you already have<br className="hidden sm:block" />
            into <span className="text-[#C75D33]">booked jobs.</span>
          </h1>

          <p className="text-lg text-slate-600 mt-5 max-w-xl leading-relaxed">
            Drop a white-label instant-quote page into your website, GMB profile, or ads. Homeowners get a real price in 60 seconds — you get a lead that's already spec'd out and ready to book. <strong>Live in under 10 minutes.</strong>
          </p>

          {/* The dramatic numbers */}
          <div className="mt-9 grid grid-cols-3 gap-3 sm:gap-4 max-w-xl">
            <BigStat value="60s" label="Avg. quote time" accent="text-[#C75D33]" />
            <BigStat value="10 min" label="To launch" accent="text-amber-600" />
            {inviteToken
              ? <BigStat value="$0" label="Your first lead" accent="text-emerald-600" />
              : <BigStat value="$1" label="First 30 days" accent="text-emerald-600" />}
          </div>

          {/* The visual: funnel preview card */}
          <FunnelMockup />

          {/* Why it works */}
          <div className="mt-12">
            <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-3">Built for screen pros</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-5">Stop losing the visitors you already paid for.</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <Why
                icon={Zap}
                title="Instant pricing converts"
                desc="Homeowners are 4x more likely to book the first company that gives them a real number. Your site finally answers the question they came to ask."
              />
              <Why
                icon={LayoutDashboard}
                title="Leads come pre-spec'd"
                desc="Every lead arrives with the service, quantities, mesh choice, and a quote total already attached. You skip the discovery call and walk straight into the appointment."
              />
              <Why
                icon={BellRing}
                title='"Ready to book" hot signals'
                desc="When a homeowner taps the book button, you get an instant alert — dashboard, email, and text. Strike while they're still on the page."
              />
              <Why
                icon={Globe}
                title="Use your own traffic — any source"
                desc="Works with your existing website, Google Business Profile, Facebook ads, Yelp link, even printed QR codes. Your brand, your pricing, your customer."
              />
            </div>
          </div>

          {/* How it works — 3 steps */}
          <div className="mt-12">
            <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-3">How it works</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-6">From zero to booked jobs in three steps.</h2>
            <div className="space-y-3">
              <Step
                n="1"
                title="Set your pricing in 5 minutes"
                desc="Tell us your prices per screen, per mesh upgrade, and your service area. We've pre-loaded sensible starting points so you can launch fast and tune later."
              />
              <Step
                n="2"
                title="Drop your quote link anywhere"
                desc="You get a branded URL like yourcompany.windowscreenquote.com. Add it to your website button, Google profile, ad campaigns, social bios, or business cards."
              />
              <Step
                n="3"
                title="Work hot leads from your dashboard"
                desc="Every quote shows up in a Kanban board with the customer's info, quoted total, and 'ready to book' flag. Export to CSV, follow up, win the job."
              />
            </div>
          </div>

          {/* Coming soon — marketplace teaser (hidden on invite-claim flow — that's a "free your first lead" story, not a "founder pricing" story) */}
          {!inviteToken && (
          <div className="mt-12 rounded-3xl border-2 border-dashed border-[#C75D33]/30 bg-[#C75D33]/[0.03] p-7 sm:p-9 relative overflow-hidden">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0F172A] text-white text-[10px] font-extrabold uppercase tracking-widest mb-3">
              <Sparkles className="w-3 h-3" /> Coming 2026 · Free for founder customers
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              Bonus: local homeowners shopping right now.
            </h3>
            <p className="text-slate-600 mt-3 max-w-xl leading-relaxed">
              We run SEO funnels that capture homeowners actively searching for screen work in your zip code. Founder customers get first dibs on those leads in their service area — no bidding, no &ldquo;sold to 4 other companies,&rdquo; no per-lead fees during early access.
            </p>
            <div className="mt-5 flex items-start gap-2 text-sm text-slate-700">
              <MapPin className="w-4 h-4 text-[#C75D33] mt-0.5 shrink-0" />
              <span>Your $97/month founder rate locks in today and never goes up — including after the marketplace launches.</span>
            </div>
          </div>
          )}

          {/* FAQ-ish reassurance */}
          <div className="mt-10 grid sm:grid-cols-3 gap-3">
            <Reassure icon={Clock} title="Launch in 10 min" desc="Most pros are live before lunch." />
            <Reassure icon={ShieldCheck} title="Cancel anytime" desc="No contracts, no setup fees." />
            <Reassure icon={Lock} title="Your data, your brand" desc="We never sell your leads or contact your customers." />
          </div>
        </section>

        {/* RIGHT — the form */}
        <aside className="lg:col-span-5 order-1 lg:order-2">
          <div className="lg:sticky lg:top-24 bg-white rounded-3xl shadow-2xl shadow-slate-900/10 border border-slate-200 overflow-hidden">
            {invitePreview && !invitePreview.invalid && (
              <div className="bg-[#C75D33] text-white px-6 sm:px-7 py-4 border-b border-[#A84C28]" data-testid="signup-invite-banner">
                <div className="text-[10px] font-extrabold uppercase tracking-widest opacity-90">You're claiming a lead</div>
                <div className="font-display font-bold text-base mt-0.5">
                  ${Number(invitePreview.lead?.estimate_total || 0).toLocaleString()} job in {invitePreview.lead?.zip_code || ""}{invitePreview.lead?.state ? " · " + invitePreview.lead.state : ""}
                </div>
                <div className="text-xs opacity-90 mt-0.5">
                  This lead is routed to you free the moment you sign up.
                  {invitePreview.invite?.status !== "sent" && <span className="font-bold"> — Already claimed by another company.</span>}
                </div>
              </div>
            )}
            <div className="bg-gradient-to-br from-[#0F172A] to-black text-white p-6 sm:p-7">
              <div className="text-xs font-bold uppercase tracking-widest text-white/85 mb-1.5">Create your account</div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold leading-tight">Launch in 10 minutes.</h2>
              <p className="text-white/85 text-sm mt-2">
                {inviteToken
                  ? "Free — no card required to claim this lead. Upgrade later to unlock leads from your own funnel + marketplace."
                  : "$1 for 30 days, then $97/mo — your founder rate, locked in forever."}
              </p>
            </div>
            <form onSubmit={submit} className="p-6 sm:p-7 space-y-3.5" data-testid="signup-form">
              <Field label="Business name">
                <Input required value={form.business_name} onChange={set("business_name")} data-testid="signup-business-input" className="h-11 rounded-xl" placeholder="Acme Screens" />
              </Field>
              <Field label="Your name">
                <Input required value={form.owner_name} onChange={set("owner_name")} data-testid="signup-name-input" className="h-11 rounded-xl" placeholder="Jane Smith" />
              </Field>
              <Field label="Work email">
                <Input required type="email" value={form.email} onChange={set("email")} data-testid="signup-email-input" className="h-11 rounded-xl" placeholder="you@yourbusiness.com" />
              </Field>
              <Field label="Phone">
                <Input required type="tel" value={form.phone} onChange={set("phone")} data-testid="signup-phone-input" className="h-11 rounded-xl" placeholder="(555) 555-1234" />
              </Field>
              <Field label="Password">
                <Input required type="password" minLength={6} value={form.password} onChange={set("password")} data-testid="signup-password-input" className="h-11 rounded-xl" placeholder="Min 6 characters" />
              </Field>
              <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
                <Checkbox
                  checked={form.sms_consent}
                  onCheckedChange={(v) => setForm({ ...form, sms_consent: !!v })}
                  className="mt-0.5"
                  data-testid="signup-sms-consent"
                />
                <span className="text-[11px] text-slate-600 leading-relaxed">
                  By checking this box, I agree to receive transactional SMS from WindowScreenQuote.com about new lead alerts, account notifications, customer support, and operational updates. Message frequency varies. Message and data rates may apply. Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for assistance. SMS consent is not shared with third parties or affiliates for marketing purposes. See our{" "}
                  <Link to="/privacy" target="_blank" className="underline hover:text-slate-800">Privacy Policy</Link>{" "}and{" "}
                  <Link to="/terms" target="_blank" className="underline hover:text-slate-800">Terms</Link>.
                </span>
              </label>
              <Button
                data-testid="signup-submit-btn"
                type="submit"
                disabled={loading}
                size="lg"
                className="w-full h-16 rounded-2xl bg-[#0F172A] hover:bg-black text-white text-base sm:text-lg font-extrabold mt-4 shadow-xl shadow-[#0F172A]/30 tracking-tight"
              >
                {loading ? "Creating your account…" : <>Create account & launch <ArrowRight className="w-5 h-5 ml-2" /></>}
              </Button>
              <div className="text-center text-xs text-slate-500 mt-1 flex items-center justify-center gap-1.5">
                <Lock className="w-3 h-3" /> {inviteToken ? "No card required · Free to claim" : "Secure · Cancel any time"}
              </div>
            </form>
            <div className="px-6 sm:px-7 pb-6 sm:pb-7 -mt-3">
              <ul className="border-t border-slate-100 pt-4 space-y-2 text-sm">
                {inviteToken && <Perk text="This lead — free, no card required" />}
                <Perk text="Your white-label quote funnel — live today" />
                <Perk text="Lead dashboard with Kanban + CSV export" />
                <Perk text="Email + text + dashboard alerts on hot leads" />
                {!inviteToken && <Perk text="Early access to local marketplace leads (2026)" />}
                {inviteToken
                  ? <Perk text="Upgrade any time: $1 trial → $97/mo founder rate — unlocks every lead" />
                  : <Perk text="$97/mo founder rate — locked in forever" />}
                <Perk text="Cancel any time — no contract" />
              </ul>
            </div>
            <div className="px-6 sm:px-7 pb-6 sm:pb-7 text-center">
              <p className="text-sm text-slate-500">
                Already have an account?{" "}
                <Link to="/login" className="text-[#C75D33] font-bold hover:underline" data-testid="signup-signin-inline">Sign in</Link>
              </p>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}

function BigStat({ value, label, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5">
      <div className={`font-display text-3xl sm:text-4xl font-extrabold ${accent}`}>{value}</div>
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function Why({ icon: Icon, title, desc }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="w-10 h-10 rounded-xl bg-[#C75D33]/10 text-[#C75D33] flex items-center justify-center mb-3"><Icon className="w-5 h-5" /></div>
      <h3 className="font-display font-bold text-slate-900 text-base mb-1">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
    </div>
  );
}

function Step({ n, title, desc }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 flex gap-4 items-start">
      <div className="font-display text-2xl font-extrabold text-[#C75D33] w-9 shrink-0 leading-none mt-0.5">{n}</div>
      <div>
        <h3 className="font-display font-bold text-slate-900 text-base mb-1">{title}</h3>
        <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function Reassure({ icon: Icon, title, desc }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-start gap-3">
      <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></div>
      <div>
        <div className="font-display font-bold text-slate-900 text-sm">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5 leading-snug">{desc}</div>
      </div>
    </div>
  );
}

function Perk({ text }) {
  return (
    <li className="flex items-start gap-2 text-slate-700">
      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {text}
    </li>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

/* Funnel preview — small, simulated to give visual depth */
function FunnelMockup() {
  return (
    <div className="mt-10 relative max-w-xl">
      {/* Glow behind */}
      <div className="absolute -inset-2 bg-gradient-to-tr from-[#0F172A]/15 via-amber-400/10 to-transparent blur-2xl rounded-3xl pointer-events-none"></div>
      <div className="relative bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden">
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
          <div className="w-2.5 h-2.5 rounded-full bg-rose-400"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400"></div>
          <div className="ml-4 text-xs text-slate-500 font-mono">yourcompany.com/quote</div>
        </div>
        <div className="p-6 sm:p-7">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#C75D33]">YOUR BRAND · INSTANT QUOTE</div>
          <h3 className="font-display text-2xl font-bold text-slate-900 mt-1 leading-tight">Window Screen Repair</h3>
          <div className="text-xs text-slate-500 mt-1">Step 4 of 5 · Almost done</div>
          <div className="h-1.5 rounded-full bg-slate-100 mt-2.5 overflow-hidden">
            <div className="h-full bg-[#0F172A]" style={{ width: "80%" }}></div>
          </div>
          <div className="mt-5 rounded-2xl bg-slate-50 p-4 border border-slate-100">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Your estimated quote</div>
            <div className="font-display text-4xl font-extrabold text-slate-900 mt-1">$320<span className="text-base font-bold text-slate-400 ml-2">— total</span></div>
            <div className="text-xs text-slate-500 mt-1">8 window screens · Standard mesh</div>
          </div>
          <div className="mt-4 flex items-center gap-2.5 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
            <ShieldCheck className="w-4 h-4 text-amber-600 shrink-0" />
            <div className="text-xs text-amber-900 font-semibold">Locked-in price · No phone tag · Honest local pricing</div>
          </div>
          <button className="mt-4 w-full h-12 rounded-2xl bg-[#0F172A] text-white text-sm font-bold flex items-center justify-center gap-1.5 pointer-events-none">
            I'm Ready To Book My Appointment <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      {/* Floating notification bubble */}
      <div className="hidden sm:flex absolute -top-3 -right-3 max-w-[260px] bg-white rounded-2xl border border-slate-200 shadow-xl p-3 items-start gap-2.5">
        <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0 text-base">🔥</div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Ready to book</div>
          <div className="text-xs text-slate-900 font-semibold mt-0.5">New lead — Maria T. · $320</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Phoenix, AZ · just now</div>
        </div>
      </div>
    </div>
  );
}
